<template>
    <div 
        class="image-zoom-container" 
        :style="containerStyle"
    >
        <div ref="wrapper" class="image-wrapper" @click.stop="toggleTooltip">
            <img
                :src="url"
                :alt="alt"
                :width="defaultWidth"
                :height="defaultHeight"
                loading="lazy"
                decoding="async"
                class="zoom-trigger-image"
            />
            <div class="zoom-overlay">
                <i class="icon-zoomin3"></i>
            </div>
        </div>
        
        <Teleport to="body">
            <transition name="fade">
                <div v-if="show" class="custom-zoom-tooltip" :style="tooltipStyle" @click.stop>
                    <img :src="url" :style="zoomImageStyle" />
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script>
export default {
    name: "ImageZoomComponent",
    props: {
        url: { type: String, required: true },
        alt: { type: String, default: "" },
        defaultWidth: { type: [String, Number], default: 38 },
        defaultHeight: { type: [String, Number], default: 38 },
        zoomWidth: { type: [String, Number], default: 300 },
        zoomHeight: { type: [String, Number], default: 300 }
    },
    data() {
        return {
            show: false,
            instanceId: Math.random().toString(36).substr(2, 9),
            position: {
                top: 0,
                left: 0
            }
        }
    },
    methods: {
        updatePosition() {
            if (this.$refs.wrapper) {
                const rect = this.$refs.wrapper.getBoundingClientRect();
                this.position = {
                    top: rect.top + window.scrollY,
                    left: rect.left + rect.width + 10 + window.scrollX
                };
            }
        },
        toggleTooltip() {
            if (!this.show) {
                this.emitter.emit('image-zoom:close-others', this.instanceId);
                this.updatePosition();
                this.show = true;
                document.addEventListener('click', this.closeTooltip);
                window.addEventListener('scroll', this.updatePosition, true);
                window.addEventListener('resize', this.updatePosition);
            } else {
                this.closeTooltip();
            }
        },
        closeTooltip() {
            this.show = false;
            document.removeEventListener('click', this.closeTooltip);
            window.removeEventListener('scroll', this.updatePosition, true);
            window.removeEventListener('resize', this.updatePosition);
        }
    },
    mounted() {
        this.emitter.on('image-zoom:close-others', (id) => {
            if (id !== this.instanceId) {
                this.closeTooltip();
            }
        });
    },
    beforeUnmount() {
        this.emitter.off('image-zoom:close-others');
        this.closeTooltip();
    },
    computed: {
        containerStyle() {
            return {
                width: `${this.defaultWidth}px`,
                height: `${this.defaultHeight}px`,
                position: 'relative',
                display: 'inline-block'
            };
        },
        tooltipStyle() {
            return {
                position: 'absolute',
                top: `${this.position.top}px`,
                left: `${this.position.left}px`,
                zIndex: 99999,
                backgroundColor: 'white',
                padding: '2px',
                borderRadius: '4px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                border: '1px solid #ddd',
                width: `${this.zoomWidth}px`
            };
        },
        zoomImageStyle() {
            return {
                width: '100%',
                height: 'auto',
                maxHeight: `${this.zoomHeight}px`,
                display: 'block',
                objectFit: 'contain'
            };
        }
    }
}
</script>

<style scoped>
.image-zoom-container {
    display: inline-block;
}
.image-wrapper {
    position: relative;
    cursor: zoom-in;
    line-height: 0;
}
.zoom-trigger-image {
    object-fit: cover;
    display: block;
}
.zoom-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
}
.image-wrapper:hover .zoom-overlay {
    opacity: 1;
}
.zoom-overlay i {
    color: white;
    font-size: 1rem;
    text-shadow: 0 0 3px rgba(0,0,0,0.5);
}
.fade-enter-active, .fade-leave-active {
    transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}
</style>
